import type { Distance, Point } from "@pdfking/math"
import { Hypot, Scale, Subtract, Total } from "@pdfking/math"
import { ZERO_DISTANCE } from "@pdfking/math/constants"
import { RecordMerge } from "@pdfking/tfunc"

import type { FolderFlapOptions, FolderSide } from "#internal"

import { FolderAt } from "./FolderAt"
import { FolderChain } from "./FolderChain"
import { FolderCreases } from "./FolderCreases"
import { FolderStraight } from "./FolderStraight"

const DEFAULTS: FolderFlapOptions = {
  folded: true,
  gusset: ZERO_DISTANCE,
  middle: false,
  taper: [ZERO_DISTANCE, ZERO_DISTANCE],
  round: [ZERO_DISTANCE, ZERO_DISTANCE],
  bevel: [false, false],
  start: FolderStraight,
  tip: FolderStraight,
  end: FolderStraight,
}

/** A flap `depth` deep folded off the edge it grows from — a pocket, a side
 * flap, a glue tab, a whole panel —, past a gusset if it has one: its
 * corners, its sides and tip as `options` draw them, and the folds of its
 * base. */
export const FolderFlap =
  (depth: Distance, options: Partial<FolderFlapOptions> = {}): FolderSide =>
  (a, b) => {
    const { folded, gusset, middle, taper, round, bevel, start, tip, end } = RecordMerge(
      DEFAULTS,
      options
    )
    const outward = Total(gusset, depth)
    if (outward <= 0) return FolderStraight(a, b)
    const length = Hypot(b.x - a.x, b.y - a.y)
    // Along the edge, and out of the die.
    const [ux, uy] = [(b.x - a.x) / length, (b.y - a.y) / length]
    const [nx, ny] = [uy, -ux]
    const at = (p: Point<Distance>, along: Distance, out: Distance): Point<Distance> => ({
      x: Total(p.x, Scale(along, ux), Scale(out, nx)),
      y: Total(p.y, Scale(along, uy), Scale(out, ny)),
    })
    const [tip_a, tip_b] = [at(a, taper[0], outward), at(b, Scale(taper[1], -1), outward)]
    // A point the gusset's share of the way from `p` to `q`.
    const across = (p: Point<Distance>, q: Point<Distance>): Point<Distance> => ({
      x: Total(p.x, Scale(Subtract(q.x, p.x), gusset / outward)),
      y: Total(p.y, Scale(Subtract(q.y, p.y), gusset / outward)),
    })
    // Where the gusset's outer fold meets each side.
    const [gusset_a, gusset_b] = [
      bevel[0] ? across(a, tip_a) : at(a, ZERO_DISTANCE, gusset),
      bevel[1] ? across(b, tip_b) : at(b, ZERO_DISTANCE, gusset),
    ]
    return FolderChain(
      FolderAt(a.x, a.y),
      start(gusset_a, tip_a),
      FolderAt(tip_a.x, tip_a.y, round[0]),
      tip(tip_a, tip_b),
      FolderAt(tip_b.x, tip_b.y, round[1]),
      end(tip_b, gusset_b),
      FolderAt(b.x, b.y),
      {
        corners: [],
        cut: [],
        fold: folded ? FolderCreases([a, b], [gusset_a, gusset_b], middle) : [],
      }
    )
  }
